import { Form as AntdForm, type FormInstance, type FormItemProps } from 'antd'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  type ReactNode,
  useContext,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import styled from 'styled-components'
import type {
  IncidentCaseV2StepHandle,
  IncidentCaseV2StepProps,
  IncidentCaseV2StepValues
} from './incidentCaseV2Steps'
import { stepFieldErrors, stepGroupGap } from './stepLayout'

type IncidentCaseV2StepFormProps<FormValues> = Pick<
  IncidentCaseV2StepProps,
  'handleRef' | 'onDirtyChange'
> & {
  initialValues: FormValues
  /** The form's values as the step saves them. Must be pure. */
  toStepValues: (values: FormValues) => IncidentCaseV2StepValues
  testId: string
  children: ReactNode
  /** The step's own form, when it reads or sets values outside its fields. */
  form?: FormInstance<FormValues>
  /** Changes that aren't step values, such as queued files: they count as unsaved. */
  hasPendingChanges?: () => boolean
} & Pick<IncidentCaseV2StepHandle, 'afterSave' | 'beforeSave'>

const HasValidatedContext = createContext(false)

/**
 * When a list of entries checks its rules: only once Next has validated the
 * step, then as it changes. Removing the last entry then doesn't flag the list
 * before the user has tried to move on.
 */
export const useListValidateTrigger = () =>
  useContext(HasValidatedContext) ? 'onChange' : []

/** A list of entries' form item, with no label of its own. */
export const StepListItem = (props: FormItemProps) => (
  <AntdForm.Item {...props} validateTrigger={useListValidateTrigger()} />
)

/** What can take focus in a field: its input, picker, radio or Add button. */
const FOCUSABLE =
  'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Brings the first field in error into view and focuses it, so the user sees
 * why Next stayed put. The first in the page, not in antd's error order.
 */
const focusFirstError = (container: HTMLElement | null) => {
  const field = container?.querySelector<HTMLElement>(
    '.ant-form-item-has-error'
  )
  if (!field) {
    return
  }
  field.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
  // A list names the control that fixes its error, e.g. an entry's Edit.
  const target =
    field.querySelector<HTMLElement>('[data-focus-on-error]') ??
    field.querySelector<HTMLElement>(FOCUSABLE)
  target?.focus({ preventScroll: true })
}

/**
 * The form every v2 step renders its fields in. It owns what is the same on
 * every step, so a step supplies only its initial values, how they map to what
 * is saved, and its fields:
 *
 * - the step handle: validate the fields, and read the values to save;
 * - whether the step has changed since it was last saved, by comparing the
 *   values it would save now with the ones last sent.
 */
const IncidentCaseV2StepForm = <FormValues extends object>({
  handleRef,
  onDirtyChange,
  initialValues,
  toStepValues,
  testId,
  children,
  form: stepForm,
  hasPendingChanges = () => false,
  afterSave,
  beforeSave
}: IncidentCaseV2StepFormProps<FormValues>) => {
  const [ownForm] = AntdForm.useForm<FormValues>()
  const form = stepForm ?? ownForm
  const savedValues = useRef(toStepValues(initialValues))
  const [hasValidated, setHasValidated] = useState(false)
  const groupsRef = useRef<HTMLDivElement>(null)

  const currentValues = () =>
    toStepValues(form.getFieldsValue(true) as FormValues)
  const isDirty = () =>
    !isEqual(currentValues(), savedValues.current) || hasPendingChanges()

  useImperativeHandle(handleRef, () => ({
    validate: () => {
      setHasValidated(true)
      return form.validateFields().then(
        () => true,
        () => {
          // The error styles land on the next render.
          setTimeout(() => focusFirstError(groupsRef.current))
          return false
        }
      )
    },
    getValues: currentValues,
    changesLog: () => !isEqual(currentValues().log, savedValues.current.log),
    markSaved: (sentValues) => {
      savedValues.current = sentValues
      const isStillDirty = isDirty()
      onDirtyChange(isStillDirty)
      return isStillDirty
    },
    afterSave,
    beforeSave
  }))

  return (
    <AntdForm
      form={form}
      layout="vertical"
      requiredMark={false}
      initialValues={initialValues}
      onValuesChange={() => onDirtyChange(isDirty())}
      data-testid={testId}
    >
      <HasValidatedContext.Provider value={hasValidated}>
        <Groups ref={groupsRef}>{children}</Groups>
      </HasValidatedContext.Provider>
    </AntdForm>
  )
}

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${stepGroupGap};
  ${stepFieldErrors}
`

export default IncidentCaseV2StepForm
